import {createUcmAlertViewGenerator, UcmAlertTypeKey, UcmAlertViewGenerator} from '@mw/ucm-alert-view';
import utils from './utils';
import {getLangCode} from '../stringBundle';

/**
 * Maps Member-API alertTypeName strings to the corresponding UcmAlertTypeKey
 * used by the @mw/ucm-alert-view library.
 *
 * These display-name strings are set by Member-API's alert.wrapper.js getTypeKey()
 * and come back on every alert detail response as the `alertTypeName` field.
 */
const ALERT_TYPE_NAME_TO_KEY: Record<string, UcmAlertTypeKey> = {
  'Tax Lien': 'UCM_TAX_LIEN',
  'Suit or Judgment': 'UCM_SUIT_OR_JUDGEMENT',
  'Change of Address': 'UCM_CHANGE_OF_ADDRESS',
  'New Account': 'UCM_NEW_ACCOUNT',
  Collection: 'UCM_COLLECTION',
  Bankruptcy: 'UCM_BANKRUPTCY',
};

/**
 * Returns the UcmAlertTypeKey for a given alertTypeName, or undefined if this
 * is not a UCM alert type handled by the library.
 */
export function resolveUcmAlertTypeKey(alertTypeName: string): UcmAlertTypeKey | undefined {
  return ALERT_TYPE_NAME_TO_KEY[alertTypeName];
}

/** Values accepted by `@mw/ucm-alert-view` as `UcmAlertInput.alertStatus` for New Account. */
export type UcmGeneratorAlertStatus = 'Acknowledged' | 'Disputed';

/**
 * Derives `alertStatus` for `createUcmAlertViewGenerator().generate()` from Member API alert detail fields.
 *
 * Uses `status` (new detail-view model) and `disposition` (`LEGIT` / `NOT_LEGIT` from legacy `AlertWrapper`).
 */
export function getUcmGeneratorAlertStatus(alertDetail: $TSFixMe): UcmGeneratorAlertStatus | undefined {
  if (!alertDetail) return undefined;

  // alert detail might contain a "status" field (alert-view response), or "disposition" field f(non-detailView)
  // legacy response has disposition field with "LEGIT"|"NOT_LEGIT"|undefined
  // newer alert-view response has "status" field with "disputed"|"investigation"|"acknowledged", etc...
  const status = utils.safeGet(alertDetail, 'status', '');
  const disposition = utils.safeGet(alertDetail, 'disposition', '');
  const isAcknowledged = disposition === 'LEGIT' || status === 'acknowledged';
  const isDisputed = status === 'disputed' || status === 'investigation' || disposition === 'NOT_LEGIT';

  return isAcknowledged ? 'Acknowledged' : isDisputed ? 'Disputed' : undefined;
}

/**
 * Builds a UcmAlertViewGenerator pre-loaded with app-level ambient configuration.
 *
 * - langCode:           current UI locale
 * - GENERAL_SUPPORT_PHONE: from window config (same phone shown in alert footer copy)
 * - navUrls:           built from window.REACT_APP_MEMBER_PORTAL_HOST
 */
export function buildUcmGenerator(): UcmAlertViewGenerator {
  const langCode = getLangCode();
  const host = (window as $TSFixMe).REACT_APP_MEMBER_PORTAL_HOST ?? '';

  return createUcmAlertViewGenerator({
    langCode,
    partnerConfig: {
      GENERAL_SUPPORT_PHONE: (window as $TSFixMe).REACT_APP_GENERAL_SUPPORT_PHONE ?? '',
    },
    navUrls: {
      credit: `${host}?redirectUrl=%2Fcredit#/credit`,
      creditFreeze: `${host}?redirectUrl=%2Flocks#/locks`,
    },
  });
}

/**
 * Returns a UcmMemberInfo object sourced from the Redux auth state.
 *
 * - firstName:        used in New Account header copy for Disputed/Acknowledged views.
 *                     Prefers ownerFirstName on the alert detail (handles sub-members)
 *                     and falls back to the primary member's firstName.
 * - isMinor:          reserved for future copy variants; always false for primary members.
 * - hasCreditReports: drives the "review credit report" link in New Account next-steps footer.
 */
export function buildUcmMemberInfo(
  authState: $TSFixMe,
  alertDetail?: $TSFixMe
): {isMinor: boolean; firstName: string; hasCreditReports: boolean} {
  const ownerFirstName = utils.safeGet(alertDetail, 'ownerFirstName', '');
  const primaryFirstName = utils.safeGet(authState, 'user.primaryMember.firstName', '');
  const firstName: string = ownerFirstName || primaryFirstName;

  const productFeatures = utils.safeGet(authState, 'user.primaryMember.plan.productFeatures', {});
  const hasCreditReports = utils.creditReportsFeatureAvailable(productFeatures);

  return {isMinor: false, firstName, hasCreditReports};
}
